import { useState, useCallback } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ProjectSelector from '../ProjectSelector';
import ArtifactTypeSelector from '../../../../components/ArtifactTypeSelector';

function FileUpload({ artifactType, onArtifactTypeChange, onDocumentIngestion, selectedProject, onProjectSelect }) {
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles);
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (file) {
      onDocumentIngestion(file.name);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    }
  });

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 180px)', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      backgroundColor: '#dee2e6',
      py: 1
    }}>
      <Box sx={{
        width: '98%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'navy.main',
        py: 3,
        flexGrow: 1,
        borderRadius: 2
      }}>
        <ProjectSelector 
          onProjectSelect={onProjectSelect} 
          initialValue={selectedProject}
        />
        <ArtifactTypeSelector 
          value={artifactType} 
          onChange={onArtifactTypeChange} 
        />
        <Paper
          {...getRootProps()}
          sx={{
            width: '100%',
            maxWidth: 700,
            height: 350,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: '2px dashed',
            borderColor: isDragActive ? '#2B5587' : '#ccc',
            backgroundColor: isDragActive ? 'rgba(43, 85, 135, 0.05)' : 'transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: '#2B5587',
              backgroundColor: 'rgba(43, 85, 135, 0.05)',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom color="text.main">
            {isDragActive
              ? 'Drop the files here'
              : 'Drag and drop files here, or click to select files'}
          </Typography>
          <Typography variant="body2" color="text.main">
            Supported formats: PDF
          </Typography>
        </Paper>

        {files.length > 0 && (
          <Box sx={{ mt: 3, width: '100%', maxWidth: 800 }}>
            <Typography variant="h6" gutterBottom color="text.main">
              Selected Files:
            </Typography>
            {files.map((file, index) => (
              <Paper
                key={index}
                sx={{
                  p: 1,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1">{file.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}> 
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedProject || !file}
            sx={{
              fontWeight: 500,
              px: 4,
              py: 1,
              fontSize: '1rem',
              '&:hover': {
                bgcolor: '#1a3a5f',
              },
              '&:active': {
                bgcolor: '#142d4a',
              },
              '&.Mui-disabled': {
                bgcolor: '#cccccc',
                color: '#666666',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Ingest Artifacts
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default FileUpload; 